'use client';

import {
  activeChips,
  type AttractionFilters,
  type FilterChip,
  type ListingFacets,
} from '../config';
import { useAttractionMoney } from './Money';

interface FilterChipsProps {
  filters: AttractionFilters;
  facets: ListingFacets;
  /** Chip Clearing — removes ONE selection, leaving the others and the sort alone. */
  onRemove: (chip: FilterChip) => void;
  /** Reset — clears every filter AND restores the default recommendation order. */
  onReset: () => void;
}

/**
 * The active-filter chip row (BCP-2063 §7).
 *
 * Chips are DERIVED from filter state on every render by `activeChips`, never
 * stored. That is the whole mechanism behind "chips and checkbox states stay in
 * sync at all times": there is only one copy of the selection, so the chip row
 * and the sidebar cannot disagree — not after a chip is cleared, not after the
 * More popup's Apply, and not after Reset.
 *
 * Renders nothing at all when no filter is applied, rather than an empty bar.
 */
export function FilterChips({ filters, facets, onRemove, onReset }: FilterChipsProps) {
  const { toUserAmount, currencySymbol } = useAttractionMoney();
  // Whole units with the selected currency's symbol — the same shape the Shows
  // chip row uses (`EventsFilters`: `${currencySymbol} ${chipMin} – …`).
  const chips = activeChips(
    filters,
    facets,
    (amount, currency) => `${currencySymbol} ${Math.round(toUserAmount(amount, currency))}`,
  );
  if (!chips.length) return null;

  return (
    <div className="atr-chips" aria-label="Active filters">
      {chips.map((chip) => (
        <span key={chip.id} className="inline-flex items-center gap-1 bg-purple-light text-primary text-xs font-medium px-3 py-1 rounded-full border border-primary/20">
          {chip.label}
          <button
            type="button"
            onClick={() => onRemove(chip)}
            aria-label={`Remove filter ${chip.label}`}
          >
            ✕
          </button>
        </span>
      ))}

      {/*
        Reset clears BOTH dimensions of state. Every sort ticket ends its Full
        Reset rule at "restores default recommendations", and default
        recommendations IS the default sort — so restoring it necessarily resets
        the sort too. BCP-2061 says so outright ("clears all filters and sorts").
      */}
      <button type="button" className="text-xs text-link-blue hover:underline font-medium" onClick={onReset}>
        Reset all
      </button>
    </div>
  );
}
